/**
 * SystemBoard — the fleet drawn as a circuit board, live.
 *
 * Every service is a chip. The EVENT BUS is the wide trace across the middle that every chip is
 * soldered to; the cognitive spine and the market path are drawn as their own point-to-point traces
 * in the order data really moves (see shared/src/topics.py and Control-Service/start_core.sh).
 * A trace is CARRYING (lit and moving) only when the bus has shown traffic for it in the last
 * CARRY_WINDOW_MS: its topic for a point-to-point trace, an event from that service for a bus stub.
 * Both ends answering with no traffic seen is IDLE (dim), not carrying: "both ends are up" was
 * counted as carrying before, which said data was flowing when nothing proved it. Store links have
 * no traffic on the bus to observe, so they can only be reachable or broken.
 */
import React, { useMemo } from 'react';
import { INFRA_SERVICES, SERVICE_REGISTRY, type ServiceDefinition } from '@/config/serviceRegistry';
import type { LiveStatus, TrafficEvent, Transport } from '@/hooks/useFleetLive';
import './SystemBoard.css';

const W = 1200, H = 560;
const CW = 150, CH = 46;                 // chip size
const BUS_Y = 132, BUS_H = 22;           // the event bus trace
const COL = [40, 222, 404, 586, 768, 950];
const ROW = { top: 34, spine: 214, support: 306, edge: 398, stores: 490 };

interface Place { x: number; y: number; }
const LAYOUT: Record<string, Place> = {
  // market path and seeing
  'market-ingestion': { x: COL[1], y: ROW.top },
  'candle-constructor': { x: COL[2], y: ROW.top }, 'perception': { x: COL[3], y: ROW.top },
  'shape-engine': { x: COL[4], y: ROW.top }, 'schema-registry': { x: COL[5], y: ROW.top },
  // cognitive spine, in stage order
  'ontology': { x: COL[0], y: ROW.spine }, 'knowledge-graph': { x: COL[1], y: ROW.spine },
  'meaning-engine': { x: COL[2], y: ROW.spine }, 'reasoning-engine': { x: COL[3], y: ROW.spine },
  'learning-engine': { x: COL[4], y: ROW.spine }, 'policy-engine': { x: COL[5], y: ROW.spine },
  // support
  'memory': { x: COL[0], y: ROW.support }, 'simulation': { x: COL[1], y: ROW.support },
  'explanation-engine': { x: COL[2], y: ROW.support }, 'language-intelligence': { x: COL[3], y: ROW.support },
  'core-brain': { x: COL[4], y: ROW.support }, 'brain-monitor': { x: COL[5], y: ROW.support },
  // edge
  'gateway': { x: COL[0], y: ROW.edge }, 'auth-system': { x: COL[1], y: ROW.edge }, 'security-core': { x: COL[2], y: ROW.edge },
  // operations: the fleet's own watchers and controller
  'topology-hub': { x: COL[3], y: ROW.edge }, 'control-service': { x: COL[4], y: ROW.edge }, 'agent-fabric': { x: COL[5], y: ROW.edge },
  // data stores
  'clickhouse': { x: COL[2], y: ROW.stores }, 'postgres': { x: COL[3], y: ROW.stores },
  'redis': { x: COL[4], y: ROW.stores }, 'mongo': { x: COL[5], y: ROW.stores },
};
const LABELS: { text: string; x: number; y: number }[] = [
  { text: 'MARKET DATA  →  SEEING', x: COL[0], y: ROW.top - 10 },
  { text: 'COGNITIVE SPINE  ·  ONTOLOGY → KNOWLEDGE → MEANING → REASONING → KERNEL → POLICY', x: COL[0], y: ROW.spine - 10 },
  { text: 'MEMORY · SIMULATION · EXPLANATION · LANGUAGE', x: COL[0], y: ROW.support - 10 },
  { text: 'EDGE', x: COL[0], y: ROW.edge - 10 },
  { text: 'OPERATIONS', x: COL[3], y: ROW.edge - 10 },
  { text: 'DATA STORES', x: COL[2], y: ROW.stores - 10 },
];
/** Point-to-point traces: [from, to, what flows, the bus topic(s) that prove it is flowing]. */
const LINKS: [string, string, string, RegExp][] = [
  ['market-ingestion', 'candle-constructor', 'ticks → candles', /^(ticks?|market\.ticks?|candles)(\.|$)/],
  ['candle-constructor', 'perception', 'candles', /^candles(\.|$)/],
  ['perception', 'shape-engine', 'perceived structure', /^perception\./],
  ['ontology', 'knowledge-graph', 'spine: classified snapshot', /^spine\.ontology/],
  ['knowledge-graph', 'meaning-engine', 'spine: knowledge', /^spine\.knowledge/],
  ['meaning-engine', 'reasoning-engine', 'spine: meaning', /^spine\.meaning/],
  ['reasoning-engine', 'learning-engine', 'spine: reasoning', /^spine\.reasoning/],
  ['learning-engine', 'policy-engine', 'decision → policy gate', /^(decisions|brain\.decision|policy\.evaluated)/],
];
/** How recent bus traffic must be for a link to count as carrying. */
export const CARRY_WINDOW_MS = 60000;
/** Which store each service keeps its data in (drawn as a small pin on the chip, listed in the side panel). */
const STORES: Record<string, string[]> = {
  'candle-constructor': ['clickhouse'], 'market-ingestion': ['clickhouse'], 'learning-engine': ['clickhouse'],
  'simulation': ['clickhouse', 'postgres'], 'ontology': ['postgres'], 'shape-engine': ['postgres', 'redis'],
  'knowledge-graph': ['postgres'], 'auth-system': ['mongo'],
};
const NOT_ON_BUS = new Set(['postgres', 'redis', 'mongo', 'clickhouse', 'event-bus']);

/** standby: an end is down by design (STANDBY), so the link is quiet but nothing is broken. */
export type LinkState = 'carrying' | 'idle' | 'reachable' | 'broken' | 'standby';
export interface BoardLink { from: string; to: string; label: string; state: LinkState; live: boolean; broken: string[]; }

interface Props {
  statuses: Record<string, LiveStatus>;
  lastSeen: Record<string, number>;
  /** Bus events seen by this page (newest first). Drives "carrying". */
  traffic?: TrafficEvent[];
  onSelect?: (service: ServiceDefinition) => void;
}

const ALL = [...SERVICE_REGISTRY, ...INFRA_SERVICES];
const BY_ID = new Map(ALL.map(s => [s.id, s]));
const up = (statuses: Record<string, LiveStatus>, id: string) => statuses[id] === 'UP';
const standby = (statuses: Record<string, LiveStatus>, id: string) => statuses[id] === 'STANDBY';
const stateText = (st: LiveStatus) => st === 'UP' ? 'OK' : st === 'DOWN' ? 'DOWN' : st === 'STANDBY' ? 'STANDBY' : '···';
const stateTitle = (st: LiveStatus) => st === 'UP' ? 'answering' : st === 'DOWN' ? 'not answering'
  : st === 'STANDBY' ? 'standby by design (not started in this profile)' : 'checking';

const topicSeen = (traffic: TrafficEvent[], re: RegExp, now: number) =>
  traffic.some(ev => now - ev.at < CARRY_WINDOW_MS && re.test(ev.topic));
const sourceSeen = (lastSeen: Record<string, number>, id: string, now: number) =>
  lastSeen[id] !== undefined && now - lastSeen[id] < CARRY_WINDOW_MS;

/** Every connection on the board: carrying (traffic seen), idle (both ends up, no traffic seen),
 *  reachable (store links: traffic not observable), or broken (and which end breaks it). */
export function boardLinks(statuses: Record<string, LiveStatus>, traffic: TrafficEvent[] = [],
                           lastSeen: Record<string, number> = {}, now = Date.now()): BoardLink[] {
  const name = (id: string) => BY_ID.get(id)?.name ?? id;
  const mk = (from: string, to: string, label: string, flowing: boolean | null): BoardLink => {
    const broken = [from, to].filter(id => !up(statuses, id) && !standby(statuses, id)).map(name);
    const resting = [from, to].some(id => standby(statuses, id));
    const state: LinkState = broken.length ? 'broken' : resting ? 'standby'
      : flowing === null ? 'reachable' : flowing ? 'carrying' : 'idle';
    return { from, to, label, state, live: state === 'carrying', broken };
  };
  const bus = Object.keys(LAYOUT).filter(id => !NOT_ON_BUS.has(id))
    .map(id => mk(id, 'event-bus', 'publish / subscribe', sourceSeen(lastSeen, id, now)));
  const direct = LINKS.map(([a, b, label, re]) => mk(a, b, label, topicSeen(traffic, re, now)));
  const stores = Object.entries(STORES).flatMap(([svc, list]) => list.map(st => mk(svc, st, 'reads / writes', null)));
  return [...direct, ...bus, ...stores];
}

export const SystemBoard: React.FC<Props> = ({ statuses, lastSeen, traffic = [], onSelect }) => {
  const now = Date.now();
  const busUp = up(statuses, 'event-bus');
  const chips = useMemo(() => ALL.filter(s => LAYOUT[s.id]), []);

  return (
    <svg className="sb" viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label="System board: every service as a chip, the event bus across the middle, live connections lit">
      <defs>
        <pattern id="sb-grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="1" className="sb-via" />
        </pattern>
      </defs>
      <rect x="0" y="0" width={W} height={H} rx="10" className="sb-pcb" />
      <rect x="0" y="0" width={W} height={H} rx="10" fill="url(#sb-grid)" />

      {LABELS.map(l => <text key={l.text} x={l.x} y={l.y} className="sb-silk">{l.text}</text>)}

      {/* stubs: every bus-connected chip is soldered to the bus */}
      {chips.filter(s => !NOT_ON_BUS.has(s.id)).map(s => {
        const p = LAYOUT[s.id];
        const cx = p.x + CW / 2;
        const above = p.y < BUS_Y;
        const y1 = above ? p.y + CH : p.y;
        const y2 = above ? BUS_Y : BUS_Y + BUS_H;
        // chips below the spine row route around the rows above them in the column gutter
        const gutter = p.x + CW + 14;
        const d = above || p.y === ROW.spine
          ? `M${cx},${y1} L${cx},${y2}`
          : `M${p.x + CW},${p.y + CH / 2} L${gutter},${p.y + CH / 2} L${gutter},${BUS_Y + BUS_H}`;
        const bothUp = busUp && up(statuses, s.id);
        const carrying = bothUp && sourceSeen(lastSeen, s.id, now);
        return <path key={`bus-${s.id}`} d={d} className="sb-trace" data-live={carrying} data-idle={bothUp && !carrying}
          data-standby={standby(statuses, s.id)} />;
      })}

      {/* point-to-point traces */}
      {LINKS.map(([a, b, label, re]) => {
        const pa = LAYOUT[a], pb = LAYOUT[b];
        const bothUp = up(statuses, a) && up(statuses, b);
        const live = bothUp && topicSeen(traffic, re, now);
        const resting = standby(statuses, a) || standby(statuses, b);
        const y = pa.y + CH / 2;
        return (
          <g key={`${a}-${b}`}>
            <title>{`${label}: ${live ? 'carrying (bus traffic in the last minute)' : bothUp ? 'idle: both ends answer, no traffic seen' : resting ? 'standby: one end is down by design' : 'broken'}`}</title>
            <path d={`M${pa.x + CW},${y} L${pb.x},${y}`} className="sb-trace sb-trace--direct" data-live={live} data-idle={bothUp && !live}
              data-standby={resting} />
            <path d={`M${pb.x - 7},${y - 4} L${pb.x},${y} L${pb.x - 7},${y + 4}`} className="sb-arrow" data-live={live} />
          </g>
        );
      })}

      {/* the event bus */}
      <g className="sb-bus" data-live={busUp} role="button" tabIndex={0}
        onClick={() => { const s = BY_ID.get('event-bus'); if (s && onSelect) onSelect(s); }}
        onKeyDown={e => { if (e.key === 'Enter') { const s = BY_ID.get('event-bus'); if (s && onSelect) onSelect(s); } }}>
        <rect x={COL[0]} y={BUS_Y} width={COL[5] + CW - COL[0]} height={BUS_H} rx="4" />
        <text x={COL[0] + 12} y={BUS_Y + 15}>EVENT BUS  :50001</text>
        <text x={COL[5] + CW - 12} y={BUS_Y + 15} textAnchor="end">{busUp ? 'carrying' : 'not answering'}</text>
      </g>

      {/* chips */}
      {chips.map(s => {
        const p = LAYOUT[s.id];
        const st = statuses[s.id] ?? 'PROBING';
        const active = lastSeen[s.id] !== undefined && now - lastSeen[s.id] < 2500;
        const stores = STORES[s.id] ?? [];
        return (
          <g key={s.id} className="sb-chip" data-status={st} data-active={active} role="button" tabIndex={0}
            transform={`translate(${p.x},${p.y})`}
            onClick={() => onSelect?.(s)} onKeyDown={e => { if (e.key === 'Enter') onSelect?.(s); }}>
            <title>{`${s.name} · port ${s.port} · ${stateTitle(st)}`}</title>
            {[14, 38, 62, 86, 110, 134].map(x => (
              <React.Fragment key={x}>
                <rect x={x} y={-4} width="5" height="4" className="sb-pin" />
                <rect x={x} y={CH} width="5" height="4" className="sb-pin" />
              </React.Fragment>
            ))}
            <rect width={CW} height={CH} rx="5" className="sb-body" />
            <circle cx="13" cy="15" r="4" className="sb-led" />
            <text x="24" y="19" className="sb-name">{s.name}</text>
            <text x="13" y="36" className="sb-port">:{s.port}</text>
            <text x={CW - 10} y="36" textAnchor="end" className="sb-state">{stateText(st)}</text>
            {stores.map((store, i) => (
              <circle key={store} cx={CW - 12 - i * 11} cy="14" r="3.5" className="sb-store" data-live={up(statuses, store)}>
                <title>{`stores data in ${BY_ID.get(store)?.name ?? store}`}</title>
              </circle>
            ))}
          </g>
        );
      })}
    </svg>
  );
};

/** The side panel: where the state comes from, which links are broken and why, and live bus traffic. */
export const LiveConnections: React.FC<{
  statuses: Record<string, LiveStatus>; transport: Transport; socketOpen: boolean; busOpen: boolean;
  traffic: TrafficEvent[]; updatedAt: number;
  lastSeen?: Record<string, number>;
}> = ({ statuses, transport, busOpen, traffic, updatedAt, lastSeen = {} }) => {
  // Recomputed every render: the fleet hook re-renders every 2 s, so links age out of "carrying".
  const links = boardLinks(statuses, traffic, lastSeen);
  const live = links.filter(l => l.state === 'carrying').length;
  const idle = links.filter(l => l.state === 'idle').length;
  const resting = links.filter(l => l.state === 'standby').length;
  const broken = links.filter(l => l.state === 'broken');
  const culprits = useMemo(() => {
    const count = new Map<string, number>();
    for (const l of broken) for (const b of l.broken) count.set(b, (count.get(b) ?? 0) + 1);
    return Array.from(count.entries()).sort((a, b) => b[1] - a[1]);
  }, [broken]);
  const name = (id: string) => BY_ID.get(id)?.name ?? id;

  return (
    <aside className="lc" aria-label="Live connections">
      <div className="lc-head">
        <h3>Live connections</h3>
        <span className="lc-pill" data-state={transport === 'websocket' ? 'live' : 'fallback'}>
          <span className="lc-dot" aria-hidden />{transport === 'websocket' ? 'WebSocket' : 'Fleet probe'}
        </span>
      </div>
      <p className="lc-source">
        {transport === 'websocket'
          ? 'Status is pushed by the control service over a WebSocket.'
          : 'The control service socket is not connected, so status comes from the browser’s own probe. It reconnects by itself.'}
        {updatedAt ? ` Last frame ${new Date(updatedAt).toLocaleTimeString()}.` : ''}
      </p>

      <div className="lc-figures">
        <div><strong className={live ? 'lc-ok' : ''}>{live}</strong><span>carrying</span></div>
        <div><strong>{idle}</strong><span>idle</span></div>
        {resting > 0 && <div><strong className="lc-standby">{resting}</strong><span>standby</span></div>}
        <div><strong className={broken.length ? 'lc-bad' : ''}>{broken.length}</strong><span>broken</span></div>
        <div><strong>{links.length}</strong><span>links</span></div>
      </div>

      <h4>What is breaking them</h4>
      {culprits.length === 0 ? (
        <p className="lc-empty">No link is broken. Carrying means bus traffic was seen in the last {CARRY_WINDOW_MS / 1000} s; idle means both ends answer but nothing was seen.</p>
      ) : (
        <ul className="lc-list">
          {culprits.slice(0, 7).map(([svc, n]) => (
            <li key={svc}><span>{svc}</span><span className="lc-bad">{n} {n === 1 ? 'link' : 'links'}</span></li>
          ))}
          {culprits.length > 7 && <li className="lc-more">and {culprits.length - 7} more services</li>}
        </ul>
      )}

      <h4>Traffic on the bus</h4>
      {!busOpen ? (
        <p className="lc-empty">The event bus stream is not connected, so no traffic can be shown.</p>
      ) : traffic.length === 0 ? (
        <p className="lc-empty">Connected. Nothing has been published since this page opened.</p>
      ) : (
        <ul className="lc-list lc-traffic">
          {traffic.slice(0, 10).map(ev => (
            <li key={ev.id}>
              <span className="lc-time">{new Date(ev.at).toLocaleTimeString()}</span>
              <span className="lc-topic" title={ev.topic}>{ev.topic}</span>
              <span className="lc-from">{ev.source ? name(ev.source) : '—'}</span>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
};

export default SystemBoard;
